import { Box, Grid, GridItem } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import type { SingleValue } from 'chakra-react-select';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import * as yup from 'yup';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftInput from '@/components/Atoms/FaceliftInput';
import FaceliftMessage from '@/components/Atoms/FaceliftMessage';
import FaceliftSelect from '@/components/Atoms/FaceliftSelect/FaceliftSelect';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import FaceliftDatePicker, {
	getTodayInNYTimezone,
} from '@/components/Molecules/FaceliftDatePicker';
import type { ExitConfirmationDraftAction } from '@/components/Molecules/FaceliftDialogWithExitConfirmation';
import FaceliftSuccessProgressModal from '@/components/Molecules/FaceliftSuccessProgressModal';
import Show from '@/components/Molecules/Show';
import { getQCodeFromProductNameAndQCode } from '@/components/Templates/BenefitVerification/BenefitVerificationTable/bv';
import { DRAFT_TYPE_LABELS, FORM_TYPES, shippingMethodItems } from '@/constants';
import {
	BgProcessModule,
	useCreateBgProcess,
	useUpdateBgProcess,
} from '@/hooks/service-hooks/bgProcess';
import { useSaveDraft } from '@/hooks/service-hooks/drafts';
import { useGetPatientsWithApprovedBV } from '@/hooks/service-hooks/patients';
import { useGetProductsWithSizes } from '@/hooks/service-hooks/products';
import { usePaginatedSelect } from '@/hooks/usePaginatedSelect';
import { getOrderRequestById } from '@/services/requests';
import { useAuthStore, useClinicStore } from '@/store';
import type { BVApprovedPatient } from '@/types/patient';
import type { CreateOrderRequest } from '@/types/requests';
import type { Draft, DraftFormSuccessResult } from '@/types/draft';
import { formatDate, formatProductError } from '@/utils';
import { trackCoreClick } from '@/utils/analytics';

const orderRequestSchema = yup.object().shape({
	patient: yup.mixed<SelectItem>().required('Patient is required'),
	physician: yup.mixed<SelectItem>().required('Provider is required'),
	clinic: yup.mixed<SelectItem>().required('Clinic is required'),
	shipping: yup.mixed<SelectItem>().required('Shipping method is required'),
	dos: yup.string().required('DOS is required'),
	shippingLocation: yup
		.mixed<SelectItem>()
		.required('Shipping location is required'),
	product: yup.mixed<SelectItem>().required('Product is required'),
	productSize: yup.string().required('Size is required'),
	productSizeLabel: yup.string().optional(), // Label for display in locked mode
	quantity: yup
		.number()
		.typeError('Quantity must be a number')
		.required('Quantity is required')
		.positive('Quantity must be positive')
		.integer('Quantity must be an integer')
		.default(1),
	comments: yup
		.string()
		.max(500, 'Comments must be less than 500 characters')
		.optional()
		.default(''),
	status: yup.string().default('953870000'),
	bvId: yup.string().optional(),
	orderId: yup.string().optional(),
});

export type OrderRequestFormData = yup.InferType<typeof orderRequestSchema>;

interface OrderRequestFormProps {
	mode: 'create' | 'edit' | 'reorder' | 'bvCreateOrder';
	orderId?: string;
	data?: Record<string, SelectItem>;
	existingDraft?: Draft;
	onSuccess?: (result?: DraftFormSuccessResult) => void;
	onIsDirtyChange?: (isDirty: boolean) => void;
	onExitConfirmationDraftActionChange?: (
		action: ExitConfirmationDraftAction | null,
	) => void;
}

const OrderRequestForm: React.FC<OrderRequestFormProps> = ({
	mode,
	orderId,
	data,
	existingDraft,
	onSuccess,
	onIsDirtyChange,
	onExitConfirmationDraftActionChange,
}) => {
	// Default values for a new order request (kept in a single place so isDirty works consistently)
	const initialDefaultValues: Partial<OrderRequestFormData> = {
		patient: undefined,
		physician: undefined,
		clinic: undefined,
		shipping: { value: '953870000', label: 'First Overnight (8:30 am)' },
		dos: formatDate(getTodayInNYTimezone()),
		shippingLocation: undefined,
		product: undefined,
		productSize: '',
		productSizeLabel: '',
		quantity: 0,
		comments: '',
		status: '953870000',
		bvId: undefined,
		orderId: undefined,
	};

	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
		setValue,
		watch,
		reset,
	} = useForm<OrderRequestFormData>({
		resolver: yupResolver(orderRequestSchema) as any,
		mode: 'onSubmit',
		defaultValues: initialDefaultValues,
	});

	const user = useAuthStore((state) => state.user?.userInformation);
	const userId = user?._id || null;
	const userName = user ? `${user.firstName} ${user.lastName}` : '';

	// Determine if we're in edit mode or reorder mode (for UI locking)
	const isEditMode = mode === 'edit';
	const isReorderMode = mode === 'reorder';
	const isBVCreateOrderMode = mode === 'bvCreateOrder';
	const hasExistingOrderRequest = Boolean(orderId) && (isEditMode || isReorderMode);
	const isUpdatingExistingOrderRequest = Boolean(orderId) && isEditMode;
	const shouldLockFields = isEditMode || isReorderMode;

	const {
		mutate: createBgProcess,
		isLoading: isCreating,
		isSuccess: isCreateSuccess,
		isError: isCreateError,
	} = useCreateBgProcess(() => {
		// Reset form only on successful create (not edit)
		if (!isEditMode && !isBVCreateOrderMode) {
			reset(initialDefaultValues, { keepDefaultValues: false });
		}
		setTimeout(() => {
			onSuccess?.({ action: 'submitted' });
		}, 2000);
	});

	const {
		mutate: updateBgProcess,
		isLoading: isUpdating,
		isError: isUpdateError,
		isSuccess: isUpdateSuccess,
	} = useUpdateBgProcess(() => {
		setTimeout(() => {
			onSuccess?.({ action: 'submitted' });
		}, 2000);
	});

	const isLoading = isCreating || isUpdating;

	// State for product validation errors (message and key to force re-render)
	const [productValidationError, setProductValidationError] = useState<{
		message: string;
		key: number;
	}>({ message: '', key: 0 });

	// Draft save mutation
	const { mutate: saveDraft, isLoading: isSavingDraft, isEditingDraft } =
		useSaveDraft({
			existingDraft,
			onSuccess: () => {
				toast.success(
					existingDraft
						? 'Draft updated successfully'
						: 'Draft saved successfully',
				);
				onSuccess?.({ action: 'draft_saved' });
			},
		});

	// Get success message from mutation state
	const successMessage = useMemo(() => {
		if (isCreateSuccess) {
			return 'Order request created successfully';
		}
		if (isUpdateSuccess) {
			return 'Order request updated successfully';
		}
		return '';
	}, [isCreateSuccess, isUpdateSuccess]);

	useEffect(() => {
		if (onIsDirtyChange) {
			onIsDirtyChange(!isDirty);
		}
	}, [isDirty]);

	// Load draft data
	useEffect(() => {
		if (existingDraft && !hasExistingOrderRequest) {
			try {
				reset(existingDraft.formData as OrderRequestFormData, { keepDefaultValues: false });
			} catch (error) {
				console.error('Failed to load draft data:', error);
			}
		}
	}, [existingDraft, hasExistingOrderRequest, reset]);

	// Fetch data in edit mode or reorder mode
	useEffect(() => {
		const fetchOrderData = async () => {
			if (hasExistingOrderRequest && orderId) {
				try {
					const response = await getOrderRequestById(orderId);
					const orderData = response.data;

					// Helper function for shipping method labels
					const getShippingMethodLabel = (method: number): string => {
						return (
							shippingMethodItems.find((item) => item.value === String(method))
								?.label || 'First Overnight (8:30 am)'
						);
					};

					// Transform to form format
					const formData = {
						patient: orderData.ta_BVPatient
							? {
								value: orderData.ta_BVPatient.contactid,
								label: orderData.ta_BVPatient.fullname,
							}
							: (null as any),
						physician: orderData.ta_Doctor
							? {
								value: orderData.ta_Doctor.contactid,
								label: orderData.ta_Doctor.fullname,
							}
							: (null as any),
						clinic: orderData.ta_Clinic
							? {
								value: orderData.ta_Clinic.accountid,
								label: orderData.ta_Clinic.name,
							}
							: (null as any),
						shipping: {
							value: String(orderData.ta_fedexshippingmethod || 953870000),
							label: getShippingMethodLabel(orderData.ta_fedexshippingmethod),
						},
						dos: formatDate(orderData.ta_dos),
						shippingLocation: orderData.ta_ShippingLocation
							? {
								value: orderData.ta_ShippingLocation.ta_shippinglocationsid,
								label: orderData.ta_ShippingLocation.ta_addressline
									? `${orderData.ta_ShippingLocation.ta_name} - ${orderData.ta_ShippingLocation.ta_addressline}`
									: orderData.ta_ShippingLocation.ta_name,
							}
							: (null as any),
						product: orderData.ta_SKU
							? {
								value: orderData.ta_SKU.ta_productid,
								label: orderData.ta_SKU.ta_name,
							}
							: (null as any),
						productSize:
							orderData.ta_SKU?.ta_productid || orderData._ta_sku_value || '',
						productSizeLabel: orderData.ta_SKU?.ta_size || '',
						quantity: orderData.ta_quantity || 1,
						comments: orderData.ta_comments || '',
						status: isReorderMode
							? '953870000'
							: String(orderData.ta_status || '953870000'), // Always set to 'Open' for reorder
						bvId: orderData._ta_bvid_value || '',
						orderId: orderData.ta_orderrequestid || orderId,
					};

					reset(formData, { keepDefaultValues: false });
				} catch (error) {
					console.error('Failed to fetch order data:', error);
				}
			}
		};

		fetchOrderData();
	}, [hasExistingOrderRequest, orderId, reset]);

	// Get clinics from store
	const clinics = useClinicStore((state) => state.currentClinics);

	// Build globalFilters for clinic IDs
	const globalFilters = useMemo(() => {
		const clinicIds = clinics.map((clinic) => clinic.accountid);
		return JSON.stringify([
			{
				key: '_ta_clinic_value',
				values: clinicIds,
			},
		]);
	}, [clinics]);

	// Use paginated select for patients dropdown with infinite scroll
	// This hook now returns both transformed items (for dropdown) and rawData (full BV objects)
	const {
		items: paginatedPatients,
		rawData: bvPatientsRawData,
		loading: isPatientsLoading,
		error: patientsError,
		onLoadMore: onLoadMorePatients,
	} = usePaginatedSelect<BVApprovedPatient>({
		queryHook: useGetPatientsWithApprovedBV,
		queryParams: {
			enabled: mode === 'create' || mode === 'bvCreateOrder',
			sort: '',
			globalFilters,
		},
		transform: (bv) => ({
			value: bv.ta_Patient.contactid,
			label: bv.ta_Patient.fullname,
		}),
		errorMessage: 'Failed to load patients',
		resetKey: mode,
	});

	// Handle save as draft
	const handleSaveAsDraft = useCallback(() => {
		const currentFormData = watch();
		
		// Generate title from patient name
		const titleOrReference = currentFormData.patient?.label || 'Untitled Draft';
		
		saveDraft({
			userId: userId || '',
			userName,
			formType: FORM_TYPES.ORDER_REQUEST_PATIENT,
			draftType: DRAFT_TYPE_LABELS.ORDER_REQUEST_PATIENT,
			titleOrReference,
			formData: currentFormData,
		});
	}, [saveDraft, userId, userName, watch]);

	useEffect(() => {
		if (!onExitConfirmationDraftActionChange) {
			return;
		}

		if (isEditMode || isReorderMode) {
			onExitConfirmationDraftActionChange(null);
			return;
		}

		onExitConfirmationDraftActionChange({
			label: isEditingDraft ? 'Update Draft' : 'Save as Draft',
			onClick: handleSaveAsDraft,
			loading: isSavingDraft,
			disabled: isSavingDraft || isLoading,
		});

		return () => {
			onExitConfirmationDraftActionChange(null);
		};
	}, [
		handleSaveAsDraft,
		isEditMode,
		isEditingDraft,
		isLoading,
		isReorderMode,
		isSavingDraft,
		onExitConfirmationDraftActionChange,
	]);

	const handleSubmitForm = handleSubmit((formData) => {
		trackCoreClick('submit_order_clicked', {
			source_module: 'order_request_form',
			form_mode: mode,
			order_id: formData.orderId,
			bv_id: formData.bvId,
			clinic_id: formData.clinic?.value,
			patient_id: formData.patient?.value,
			product_id: formData.product?.value,
			product_size_id: formData.productSize,
			shipping_method: formData.shipping?.value,
			quantity: formData.quantity,
		});

		if (isUpdatingExistingOrderRequest && formData.orderId) {
			// Edit mode - only send quantity and comments
			const updatePayload = {
				ta_quantity: String(formData.quantity || 1),
				ta_comments: formData.comments || '',
			};

			updateBgProcess({
				moduleName: BgProcessModule.ORDER_REQUEST_SINGLE,
				userId: userId || '',
				clinicId: formData?.clinic?.value as string,
				payload: {
					payload: updatePayload,
					additionalPayload: {
						clinicName: formData.clinic?.label as string,
						physicianLastName: (formData.physician?.label as string)
							.trim()
							.split(' ')
							.slice(-1)[0],
						patientLastName: (formData.patient?.label as string)
							.trim()
							.split(' ')
							.slice(-1)[0],
					},
				},
				crmId: formData.orderId,
			});
		} else {
			// Create mode or Reorder mode - send all fields

			// Validate selected product before submission
			if (
				(mode === 'create' || mode === 'bvCreateOrder') &&
				formData.product &&
				productsWithSizes?.items
			) {
				// Find the selected product's prior auth to get its qcode
				const selectedPriorAuth =
					selectedPatientBV?.ta_ta_benefitsverification_ta_priorauth_BV?.find(
						(pa) => pa.ta_priorauthid === formData.product?.value,
					);

				if (selectedPriorAuth?.ta_name) {
					const selectedProductQcode = getQCodeFromProductNameAndQCode(
						selectedPriorAuth.ta_name,
					);
					const apiQcodes = productsWithSizes.items.map(
						(product) => product.qcode,
					);

					// Check if selected product's qcode exists in API response
					if (!apiQcodes.includes(selectedProductQcode)) {
						// Extract product name (without qcode in parentheses)
						const productName =
							selectedPriorAuth.ta_name.indexOf('(') >= 0
								? selectedPriorAuth.ta_name
									.slice(0, selectedPriorAuth.ta_name.indexOf('('))
									.trim()
								: selectedPriorAuth.ta_name;

						// Update error with incremented key to force modal to show every time submit is clicked
						setProductValidationError((prev) => ({
							message: formatProductError(
								productName,
								selectedProductQcode,
								'selected clinic',
							),
							key: prev.key + 1,
						}));
						return; // Prevent submission
					}
				}
			}

			// Helper function to format date from MM/DD/YYYY to YYYY-MM-DD
			const formatDateToYYYYMMDD = (dateStr: string): string | undefined => {
				if (!dateStr) return undefined;
				try {
					const date = new Date(dateStr);
					if (isNaN(date.getTime())) return undefined;
					return date.toISOString().split('T')[0];
				} catch {
					return undefined;
				}
			};

			// Map form data to API payload structure
			const payload: CreateOrderRequest = {
				'ta_Clinic@odata.bind': formData.clinic?.value
					? `accounts(${formData.clinic.value})`
					: undefined,
				'ta_Doctor@odata.bind': formData.physician?.value
					? `contacts(${formData.physician.value})`
					: undefined,
				ta_fedexshippingmethod: formData.shipping?.value
					? Number(formData.shipping.value)
					: undefined,
				ta_orderrequestdate: new Date().toISOString().split('T')[0], // Current date in YYYY-MM-DD
				ta_status: formData.status ? Number(formData.status) : 953870000, // Default to 'Requested'
				'ta_BVPatient@odata.bind': formData.patient?.value
					? `contacts(${formData.patient.value})`
					: undefined,
				'ta_BVID@odata.bind': formData.bvId
					? `ta_benefitsverifications(${formData.bvId})`
					: undefined,
				'ta_SKU@odata.bind': formData.productSize
					? `ta_products(${formData.productSize})`
					: undefined,
				ta_quantity: String(formData.quantity || 1),
				statecode: 1, // Active
				statuscode: 1, // Active
				ta_comments: formData.comments || '',
				'ta_ShippingLocation@odata.bind': formData.shippingLocation?.value
					? `ta_shippinglocationses(${formData.shippingLocation.value})`
					: undefined,
				ta_dos: formatDateToYYYYMMDD(formData.dos),
			};

			createBgProcess({
				moduleName: BgProcessModule.ORDER_REQUEST_SINGLE,
				userId: userId || '',
				clinicId: formData?.clinic?.value as string,
				payload: {
					payload: {
						orderRequest: payload,
						bvPayload: {
							bvId: formData.bvId || '',
							payload: {
								// Set the BV status to 'Order Placed'
								ta_bv_status: 953870025,
								'ta_SelectedPriorAuth@odata.bind':
									selectedProductPriorAuth?.ta_priorauthid
										? `ta_priorauths(${selectedProductPriorAuth.ta_priorauthid})`
										: undefined,
							},
						},
						bvProductPayload: {
							priorAuthId: selectedProductPriorAuth?.ta_priorauthid || '',
							payload: {
								'ta_BV@odata.bind': formData.bvId
									? `ta_benefitsverifications(${formData.bvId})`
									: '',
								'ta_BVProduct@odata.bind':
									selectedProductPriorAuth?.ta_priorauthid
										? `ta_products(${selectedProductPriorAuth.productSizes?.at(0)?.ta_productid || ''})`
										: '',
								'ta_BVPhysician@odata.bind': formData.physician?.value
									? `contacts(${formData.physician.value})`
									: '',
								'ta_BVPatient@odata.bind': formData.patient?.value
									? `contacts(${formData.patient.value})`
									: '',
							},
						},
					},
					additionalPayload: {
						clinicName: formData.clinic?.label as string,
						physicianLastName: (formData.physician?.label as string)
							.trim()
							.split(' ')
							.slice(-1)[0],
						patientLastName: (formData.patient?.label as string)
							.trim()
							.split(' ')
							.slice(-1)[0],
					},
				},
			});
		}
	});

	useEffect(() => {
		if (data?.patient && mode === 'bvCreateOrder') {
			// setValue('patient', data);
			handleLoadDataForBVCreateOrder(data);
		}
	}, [data, setValue, mode, bvPatientsRawData]);

	// Derive selected patient BV from watch and raw data
	const selectedPatientBV = useMemo(() => {
		const patientValue = watch('patient')?.value;
		if (!patientValue) return null;
		return (
			bvPatientsRawData.find(
				(bv) => bv.ta_Patient.contactid === patientValue,
			) || null
		);
	}, [watch('patient'), bvPatientsRawData]);

	// Products with sizes API call (dependent on selected clinic, only in create mode)
	const { data: productsWithSizes } = useGetProductsWithSizes({
		clinicId: ((watch('clinic') as SelectItem)?.value as string) || '',
		userPort: 'Woundcare+',
		enabled:
			(mode === 'create' || mode === 'bvCreateOrder') &&
			!!watch('clinic') &&
			!!selectedPatientBV,
	});

	// Validate products when patient is selected and API data is available
	useEffect(() => {
		// Only validate in create mode
		if (mode !== 'create' && mode !== 'bvCreateOrder') {
			setProductValidationError({ message: '', key: 0 });
			return;
		}

		// Clear error if no patient selected
		if (!selectedPatientBV || !watch('patient')?.value) {
			setProductValidationError({ message: '', key: 0 });
			return;
		}

		// Wait for API data to be available
		if (!productsWithSizes?.items) {
			return;
		}

		// Extract qcodes from API response
		const apiQcodes = productsWithSizes.items.map((product) => product.qcode);

		// Extract qcodes from patient BV products
		const patientBVProducts =
			selectedPatientBV.ta_ta_benefitsverification_ta_priorauth_BV || [];

		if (patientBVProducts.length === 0) {
			setProductValidationError({ message: '', key: 0 });
			return;
		}

		// Find invalid products (products in patient BV that don't exist in API response)
		const invalidProducts: Array<{ name: string; qcode: string }> = [];

		patientBVProducts.forEach((pa) => {
			if (pa.ta_name) {
				const extractedQcode = getQCodeFromProductNameAndQCode(pa.ta_name);
				// Extract product name (without qcode in parentheses)
				const productName =
					pa.ta_name.indexOf('(') >= 0
						? pa.ta_name.slice(0, pa.ta_name.indexOf('(')).trim()
						: pa.ta_name;

				// Check if qcode exists in API response
				if (!apiQcodes.includes(extractedQcode)) {
					invalidProducts.push({
						name: productName,
						qcode: extractedQcode,
					});
				}
			}
		});

		const shouldSetError =
			invalidProducts.length > 0 && mode === 'create' && !selectedPatientBV?._ta_selectedpriorauth_value;
		// Set error message if there are invalid products
		if (shouldSetError) {
			const combinedMessage = `The following product(s) associated with the selected patient are not available for the selected clinic: ${invalidProducts.map(product=> `${product.name} (${product.qcode})`).join(', ')}. Please contact support to resolve this issue.`;
			setProductValidationError((prev) => ({
				message: combinedMessage,
				key: prev.key + 1, // Increment key to force modal to show every time
			}));
		} else {
			setProductValidationError({ message: '', key: 0 });
		}
	}, [selectedPatientBV, productsWithSizes, mode, watch('patient')?.value]);

	// Derive selected product prior auth from watch and selected patient BV
	const selectedProductPriorAuth = useMemo(() => {
		const productValue = watch('product')?.value;
		if (!productValue || !selectedPatientBV) return null;
		return (
			selectedPatientBV.ta_ta_benefitsverification_ta_priorauth_BV?.find(
				(pa) => pa.ta_priorauthid === productValue,
			) || null
		);
	}, [watch('product'), selectedPatientBV]);

	// Handle product selection
	const handleProductChange = (selectedProduct: SingleValue<SelectItem>) => {
		if (!selectedProduct) {
			setValue('product', null as any);
			setValue('productSize', '');
			return;
		}

		setValue('product', selectedProduct, { shouldDirty: true });

		// Find the prior auth for this product
		const priorAuth =
			selectedPatientBV?.ta_ta_benefitsverification_ta_priorauth_BV.find(
				(pa) => pa.ta_priorauthid === selectedProduct.value,
			);

		if (priorAuth) {
			// Handle sizes - always auto-fill first item
			const sizes = priorAuth.productSizes || [];
			if (sizes.length > 0) {
				setValue('productSize', sizes[0].ta_productid, { shouldDirty: true });
			} else {
				setValue('productSize', '', { shouldDirty: true });
			}
		}
	};

	// Handle patient selection
	const handleLoadDataForBVCreateOrder = (data: Record<string, SelectItem>) => {
		const selectedPatient = data.patient;
		const selectedAuth = data.auth;
		if (!selectedPatient) {
			setValue('patient', null as any);
			setValue('physician', null as any);
			setValue('clinic', null as any);
			setValue('product', null as any);
			setValue('productSize', '');
			setValue('shippingLocation', null as any);
			setValue('shipping', null as any);
			setValue('quantity', 1);
			return;
		}

		setValue('patient', selectedPatient, { shouldDirty: true });

		// Find the BV data for selected patient from the raw data accumulated by pagination
		const patientBV = bvPatientsRawData.find(
			(bv) => bv.ta_Patient.contactid === selectedPatient.value,
		);

		if (patientBV) {
			// Set BV ID in form (hidden field)
			setValue('bvId', patientBV.ta_benefitsverificationid, {
				shouldDirty: true,
			});

			// Auto-fill shipping method with default (First Overnight)
			if (!watch('shipping')) {
				setValue(
					'shipping',
					{
						value: '953870000',
						label: 'First Overnight (8:30 am)',
					},
					{ shouldDirty: true },
				);
			}

			// Auto-fill physician with first item
			if (patientBV.ta_Physician2) {
				const physicianOption: SelectItem = {
					value: patientBV.ta_Physician2.contactid,
					label: patientBV.ta_Physician2.fullname,
				};
				setValue('physician', physicianOption, { shouldDirty: true });
			}

			// Auto-fill clinic with first item
			if (patientBV.ta_Clinic) {
				const clinicOption: SelectItem = {
					value: patientBV.ta_Clinic.accountid,
					label: patientBV.ta_Clinic.name,
				};
				setValue('clinic', clinicOption, { shouldDirty: true });
			}

			// Handle products - filter by _ta_selectedpriorauth_value if it exists
			let availableProducts =
				patientBV.ta_ta_benefitsverification_ta_priorauth_BV || [];

			// If _ta_selectedpriorauth_value exists, only show that product
			if (patientBV._ta_selectedpriorauth_value) {
				availableProducts = availableProducts.filter(
					(pa) => pa.ta_priorauthid === patientBV._ta_selectedpriorauth_value,
				);
			}

			const product = availableProducts.find(
				(p) => p.ta_priorauthid === selectedAuth?.value,
			);
			if (product) {
				const productOption: SelectItem = {
					value: product?.ta_priorauthid,
					label: product?.ta_name,
				};
				setValue('product', productOption, { shouldDirty: true });

				// Handle product sizes - always auto-fill first item
				const sizes = product.productSizes || [];
				if (sizes.length > 0) {
					setValue('productSize', sizes[0].ta_productid, { shouldDirty: true });
				} else {
					setValue('productSize', '', { shouldDirty: true });
				}
			} else {
				setValue('product', null as any, { shouldDirty: true });
				setValue('productSize', '', { shouldDirty: true });
			}

			// Handle shipping locations - always auto-fill first item
			const shippingLocations = patientBV.ta_Clinic?.shippingLocations || [];
			if (shippingLocations.length > 0) {
				const locationOption: SelectItem = {
					value: shippingLocations[0].ta_shippinglocationsid,
					label: `${shippingLocations[0].ta_name} - ${shippingLocations[0].ta_addressline}, ${shippingLocations[0].ta_citylocality}, ${shippingLocations[0].ta_stateprovince} ${shippingLocations[0].ta_postalcode}`,
				};
				setValue('shippingLocation', locationOption, { shouldDirty: true });
			} else {
				setValue('shippingLocation', null as any, { shouldDirty: true });
			}
		}
	};
	const handlePatientChange = (selectedPatient: SingleValue<SelectItem>) => {
		if (!selectedPatient) {
			setValue('patient', null as any);
			setValue('physician', null as any);
			setValue('clinic', null as any);
			setValue('product', null as any);
			setValue('productSize', '');
			setValue('shippingLocation', null as any);
			setValue('shipping', null as any);
			setValue('quantity', 1);
			setProductValidationError({ message: '', key: 0 }); // Clear validation error when patient is cleared
			return;
		}

		setValue('patient', selectedPatient, { shouldDirty: true });

		// Find the BV data for selected patient from the raw data accumulated by pagination
		const patientBV = bvPatientsRawData.find(
			(bv) => bv.ta_Patient.contactid === selectedPatient.value,
		);

		if (patientBV) {
			// Set BV ID in form (hidden field)
			setValue('bvId', patientBV.ta_benefitsverificationid, {
				shouldDirty: true,
			});

			// Auto-fill shipping method with default (First Overnight)
			if (!watch('shipping')) {
				setValue(
					'shipping',
					{
						value: '953870000',
						label: 'First Overnight (8:30 am)',
					},
					{ shouldDirty: true },
				);
			}

			// Auto-fill physician with first item
			if (patientBV.ta_Physician2) {
				const physicianOption: SelectItem = {
					value: patientBV.ta_Physician2.contactid,
					label: patientBV.ta_Physician2.fullname,
				};
				setValue('physician', physicianOption, { shouldDirty: true });
			}

			// Auto-fill clinic with first item
			if (patientBV.ta_Clinic) {
				const clinicOption: SelectItem = {
					value: patientBV.ta_Clinic.accountid,
					label: patientBV.ta_Clinic.name,
				};
				setValue('clinic', clinicOption, { shouldDirty: true });
			}

			// Handle products - filter by _ta_selectedpriorauth_value if it exists
			let availableProducts =
				patientBV.ta_ta_benefitsverification_ta_priorauth_BV || [];

			// If _ta_selectedpriorauth_value exists, only show that product
			if (patientBV._ta_selectedpriorauth_value) {
				availableProducts = availableProducts.filter(
					(pa) => pa.ta_priorauthid === patientBV._ta_selectedpriorauth_value,
				);
			}

			if (availableProducts.length > 0) {
				const productOption: SelectItem = {
					value: availableProducts[0].ta_priorauthid,
					label: availableProducts[0].ta_name,
				};
				setValue('product', productOption, { shouldDirty: true });

				// Handle product sizes - always auto-fill first item
				const sizes = availableProducts[0].productSizes || [];
				if (sizes.length > 0) {
					setValue('productSize', sizes[0].ta_productid, { shouldDirty: true });
				} else {
					setValue('productSize', '', { shouldDirty: true });
				}
			} else {
				setValue('product', null as any, { shouldDirty: true });
				setValue('productSize', '', { shouldDirty: true });
			}

			// Handle shipping locations - always auto-fill first item
			const shippingLocations = patientBV.ta_Clinic?.shippingLocations || [];
			if (shippingLocations.length > 0) {
				const locationOption: SelectItem = {
					value: shippingLocations[0].ta_shippinglocationsid,
					label: `${shippingLocations[0].ta_name} - ${shippingLocations[0].ta_addressline}, ${shippingLocations[0].ta_citylocality}, ${shippingLocations[0].ta_stateprovince} ${shippingLocations[0].ta_postalcode}`,
				};
				setValue('shippingLocation', locationOption, { shouldDirty: true });
			} else {
				setValue('shippingLocation', null as any, { shouldDirty: true });
			}
		}
	};

	// Get physicians from selected patient BV
	const physicians: SelectItem[] = useMemo(() => {
		if (!selectedPatientBV?.ta_Physician2) return [];
		return [
			{
				value: selectedPatientBV.ta_Physician2.contactid,
				label: selectedPatientBV.ta_Physician2.fullname,
			},
		];
	}, [selectedPatientBV]);

	// Get products from selected patient BV
	const products: SelectItem[] = useMemo(() => {
		if (!selectedPatientBV?.ta_ta_benefitsverification_ta_priorauth_BV?.length || !productsWithSizes?.items?.length){
			return [];
		}

		// Filter products based on _ta_selectedpriorauth_value if it exists
		const selectedPriorAuthId = selectedPatientBV._ta_selectedpriorauth_value;

		let filteredProducts =
			selectedPatientBV.ta_ta_benefitsverification_ta_priorauth_BV.filter((pa) => productsWithSizes?.items.some(product => product.qcode === getQCodeFromProductNameAndQCode(pa.ta_name))); // Filter out products without a name

		// If _ta_selectedpriorauth_value exists, only show that product
		if (selectedPriorAuthId) {
			filteredProducts = filteredProducts.filter(
				(pa) => pa.ta_priorauthid === selectedPriorAuthId,
			);
		}
		if (mode === 'create') {
			if (filteredProducts.length > 0) {
				setValue('product', { value: filteredProducts[0].ta_priorauthid, label: filteredProducts[0].ta_name }, { shouldDirty: true });
			} else {
				setValue('product', null as any, { shouldDirty: true });
				setValue('productSize', '', { shouldDirty: true });
				setProductValidationError({ message: '', key: 0 }); // Clear validation error if no products available
			}
		}

		return filteredProducts.map((pa) => ({
			value: pa.ta_priorauthid,
			label: pa.ta_name,
		}));
	}, [selectedPatientBV, productsWithSizes]);

	// Get shipping locations from selected patient BV
	const shippingLocations: SelectItem[] = useMemo(() => {
		if (!selectedPatientBV?.ta_Clinic?.shippingLocations) return [];
		return selectedPatientBV.ta_Clinic.shippingLocations.map((loc) => ({
			value: loc.ta_shippinglocationsid,
			label: `${loc.ta_name} - ${loc.ta_addressline}, ${loc.ta_citylocality}, ${loc.ta_stateprovince} ${loc.ta_postalcode}`,
		}));
	}, [selectedPatientBV]);

	// Get product sizes from selected product
	const productSizes: SelectItem[] = useMemo(() => {
		if (!selectedProductPriorAuth?.productSizes) return [];
		return selectedProductPriorAuth.productSizes.map((size: any) => ({
			value: size.ta_productid,
			label: size.ta_size,
		}));
	}, [selectedProductPriorAuth, watch('product')]);

	// Derive locked fields based on data length
	const lockedFields = useMemo(
		() => ({
			physician: true, // Always only 1 physician per BV
			clinic: true, // Always only 1 clinic per BV
			product: products.length === 1,
			shippingLocation: shippingLocations.length === 1,
			productSize: productSizes.length === 1,
		}),
		[products, shippingLocations, productSizes],
	);

	// Watch all form values for validation
	const formValues = watch();

	// Check if all required fields are filled for submit button validation
	const isFormValid = useMemo(() => {
		// Check all required fields
		const hasPatient = !!formValues.patient?.value;
		const hasPhysician = !!formValues.physician?.value;
		const hasClinic = !!formValues.clinic?.value;
		const hasShipping = !!formValues.shipping?.value;
		const hasShippingLocation = !!formValues.shippingLocation?.value;
		const hasDos =
			!!formValues.dos &&
			formValues.dos !== '-' &&
			formValues.dos.trim() !== '';
		const hasProduct = !!formValues.product?.value;
		const hasProductSize =
			!!formValues.productSize && formValues.productSize.trim() !== '';
		const hasQuantity = formValues.quantity && Number(formValues.quantity) > 0;

		return (
			hasPatient &&
			hasPhysician &&
			hasClinic &&
			hasShipping &&
			hasShippingLocation &&
			hasDos &&
			hasProduct &&
			hasProductSize &&
			hasQuantity
		);
	}, [formValues]);

	return (
		<form onSubmit={handleSubmitForm}>
			<Box
				display="flex"
				justifyContent="center"
				px={{ base: 4, md: 6, lg: 2 }}
				py={{ base: 2, md: 4, lg: 2 }}
			>
				<Grid
					templateColumns={{
						base: '1fr',
						md: 'repeat(2, 1fr)',
						lg: '460px 280px',
					}}
					gap={{ base: 3, md: 4, lg: 3 }}
					columnGap={{ base: 0, md: 4 }}
					maxW={{ base: '100%', md: '700px', lg: '760px' }}
					w={{ base: '100%', lg: 'auto' }}
				>
					{/* Left Column on Desktop - spans 2 cols on tablet */}
					<GridItem colSpan={{ base: 1, md: 2, lg: 1 }}>
						<Box
							display="flex"
							flexDirection="column"
							gap={{ base: 3, md: 4, lg: 2 }}
						>
							{/* Patient */}
							<FaceliftSelect<SelectItem, false>
								topLabel="Patient"
								placeholder="Type Patient Name"
								value={watch('patient')}
								onChange={handlePatientChange}
								items={shouldLockFields ? [] : paginatedPatients}
								loading={isPatientsLoading}
								error={
									(errors.patient?.message as unknown as string) ||
									patientsError
								}
								isSearchable={true}
								isDisabled={shouldLockFields || isBVCreateOrderMode}
								required
								onLoadMore={onLoadMorePatients}
							/>

							{/* Provider & Clinic Row on tablet/desktop */}
							<Grid
								templateColumns={{ base: '1fr', md: '1fr 1fr' }}
								gap={{ base: 3, md: 4, lg: 3 }}
							>
								<GridItem>
									<FaceliftSelect<SelectItem, false>
										topLabel="Provider"
										placeholder="Select Provider"
										value={watch('physician')}
										onChange={(newValue: SingleValue<SelectItem>) => {
											if (newValue) {
												setValue('physician', newValue, { shouldDirty: true });
											}
										}}
										items={shouldLockFields ? [] : physicians}
										error={errors.physician?.message as unknown as string}
										isSearchable={true}
										isDisabled={
											shouldLockFields || isBVCreateOrderMode
											// lockedFields.physician
										}
										required
									/>
								</GridItem>
								<GridItem>
									<FaceliftSelect<SelectItem, false>
										topLabel="Clinic"
										placeholder="Select Clinic"
										value={watch('clinic')}
										onChange={(newValue: SingleValue<SelectItem>) => {
											if (newValue) {
												setValue('clinic', newValue, { shouldDirty: true });
											}
										}}
										items={watch('clinic') ? [watch('clinic')!] : []}
										error={errors.clinic?.message as unknown as string}
										isSearchable={false}
										isDisabled={
											shouldLockFields || isBVCreateOrderMode
											// lockedFields.clinic
										}
										required
									/>
								</GridItem>
							</Grid>

							{/* Product Selection */}
							<FaceliftSelect<SelectItem, false>
								topLabel="Product Selection"
								placeholder="Type Product Name"
								value={watch('product')}
								onChange={handleProductChange}
								items={shouldLockFields ? [] : products}
								error={errors.product?.message as unknown as string}
								isSearchable={true}
								isDisabled={
									shouldLockFields || isBVCreateOrderMode
									// lockedFields.product
								}
								required
							/>

							<GridItem order={{ base: 1, md: 2, lg: 2 }}>
								<FaceliftSelect<SelectItem, false>
									topLabel="Size"
									placeholder="Select Size"
									value={
										shouldLockFields && watch('productSize')
											? {
												value: watch('productSize'),
												label:
													watch('productSizeLabel') || watch('productSize'),
											}
											: productSizes.find(
												(s) => s.value === watch('productSize'),
											) || null
									}
									onChange={(newValue: SingleValue<SelectItem>) => {
										if (newValue) {
											setValue('productSize', String(newValue.value), {
												shouldDirty: true,
											});
											setValue('productSizeLabel', String(newValue.label), {
												shouldDirty: true,
											});
										}
									}}
									items={shouldLockFields ? [] : productSizes}
									error={errors.productSize?.message}
									isSearchable={true}
									isDisabled={
										(shouldLockFields || lockedFields.productSize) &&
										mode !== 'bvCreateOrder'
									}
									required
								/>
							</GridItem>
						</Box>
					</GridItem>

					{/* Right Column on Desktop */}
					<GridItem colSpan={{ base: 1, md: 2, lg: 1 }}>
						<Box
							display="flex"
							flexDirection="column"
							gap={{ base: 3, md: 4, lg: 2 }}
						>
							{/* DOS and Shipping Location side by side on tablet */}
							<Grid
								templateColumns={{ base: '1fr', md: '1fr 1fr', lg: '1fr' }}
								gap={{ base: 3, md: 4, lg: 2 }}
							>
								<GridItem>
									<FaceliftDatePicker
										topLabel="DOS"
										value={watch('dos')}
										onChange={(date: string) =>
											setValue('dos', date, { shouldDirty: true })
										}
										placeholder="MM/DD/YYYY"
										error={errors.dos?.message}
										disabled={shouldLockFields}
										minDate={getTodayInNYTimezone()} // Prevent past dates using NY timezone
										defaultToToday
										required
									/>
								</GridItem>
								<GridItem>
									<FaceliftSelect<SelectItem, false>
										topLabel="Shipping Location"
										placeholder="Select Location"
										value={watch('shippingLocation')}
										onChange={(newValue: SingleValue<SelectItem>) => {
											if (newValue) {
												setValue('shippingLocation', newValue, {
													shouldDirty: true,
												});
											}
										}}
										items={shouldLockFields ? [] : shippingLocations}
										error={
											errors.shippingLocation?.message as unknown as string
										}
										isSearchable={true}
										isDisabled={shouldLockFields}
										required
									/>
								</GridItem>
							</Grid>

							{/* Shipping Method and Size side by side on tablet */}
							<Grid
								templateColumns={{ base: '1fr', md: '1fr 1fr', lg: '1fr' }}
								gap={{ base: 3, md: 4, lg: 2 }}
							>
								<GridItem order={{ base: 2, md: 1, lg: 1 }}>
									<FaceliftSelect<SelectItem, false>
										topLabel="Shipping Method"
										placeholder="Select"
										value={watch('shipping')}
										onChange={(newValue: SingleValue<SelectItem>) => {
											if (newValue) {
												setValue('shipping', newValue, { shouldDirty: true });
											}
										}}
										items={shippingMethodItems}
										error={errors.shipping?.message as unknown as string}
										isSearchable={false}
										isDisabled={shouldLockFields}
										required
									/>
								</GridItem>
								<GridItem order={{ base: 2, md: 1, lg: 1 }}>
									<FaceliftInput
										topLabel="Quantity"
										placeholder="-"
										type="number"
										min="1"
										{...register('quantity')}
										error={errors.quantity?.message}
										disabled={false} // Always enabled (editable in edit mode)
										required
									/>
								</GridItem>
							</Grid>
							{/* Quantity */}
						</Box>
					</GridItem>

					{/* Comments - Full Width */}
					<GridItem colSpan={{ base: 1, md: 2 }}>
						<FaceliftInput
							topLabel="Comments"
							placeholder="Type Here"
							{...register('comments')}
							error={errors.comments?.message}
							disabled={false} // Always enabled (editable in edit mode)
						/>
					</GridItem>

					{/* Buttons - Full Width */}
					<GridItem colSpan={{ base: 1, md: 2 }}>
						<Box
							display="flex"
							flexDirection={{ base: 'column', sm: 'row' }}
							gap={3}
							justifyContent={{ base: 'center', lg: 'flex-end' }}
							mt={{ base: 0, lg: 1 }}
						>
							{!isEditMode && !isReorderMode && (
								<FaceliftButton
									minW={{ base: '100%', sm: '160px' }}
									loading={isSavingDraft}
									type="button"
									onClick={handleSaveAsDraft}
									cursor="pointer"
									disabled={isSavingDraft || isLoading}
									bg="transparent"
									color="brand.600"
									border="1px solid"
									borderColor="brand.600"
									_hover={{
										bg: 'brand.50',
									}}
									_dark={{
										_hover: {
											bg: 'whiteAlpha.100',
										},
									}}
								>
									{isEditingDraft ? 'Update Draft' : 'Save as Draft'}
								</FaceliftButton>
							)}
							<FaceliftButton
								minW={{ base: '100%', sm: '160px' }}
								loading={isLoading}
								type="submit"
								cursor="pointer"
								disabled={!isFormValid || isLoading}
							>
								{isEditMode ? 'Update' : 'Submit'}
							</FaceliftButton>
						</Box>
					</GridItem>
				</Grid>
			</Box>

			<FaceliftSuccessProgressModal
				open={
					isLoading ||
					isCreateSuccess ||
					isUpdateSuccess ||
					isCreateError ||
					isUpdateError
				}
				isLoading={isLoading}
				isSucceed={isCreateSuccess || isUpdateSuccess}
				hasError={isCreateError || isUpdateError}
				onOpenChange={() => {}}
				title={successMessage}
				errorMessage={
					isCreateError
						? 'Unable to create order. Please check your information and try again.'
						: isUpdateError
							? 'Unable to update order. Please try again.'
							: ''
				}
			/>

			{/* Product Validation Error Message */}
			<FaceliftMessage
				key={productValidationError.key}
				message={productValidationError.message}
				error={true}
				messageTitle="Product(s) not found"
				timeout={0}
			/>
		</form>
	);
};

export default OrderRequestForm;
